import { EDDProviderType } from "@/enums/eddProvider"

export interface ITrackingPageOptions {
  displayOption: DisplayOption | undefined;
  pageTranslations: PageTranslations | undefined;
  autoTranslate: AutoTranslate | undefined;
  pageTranslationsD: PageTranslations | undefined;
  customOrderStatus: CustomOrderStatus | undefined;
  customOrderInput: CustomOrderInput | undefined;
  post_purchase_edd: PostPurchaseEDD | undefined;
  post_edd_providers: PostEddProviders | undefined;
  proRecommendAndUpsell: ProRecommendAndUpsell | undefined;
  additionalTextSetting: AdditionalTextSetting | undefined;
  seoOptimization: SEOOptimization | undefined;
  dateAndTimeFormat: DateAndTimeFormat | undefined;
  translateTrackingDetailedInfo: TranslateTrackingDetailedInfo[] | undefined;
  customCssAndHtml: CustomCSSAndHTML | undefined;
  brandingConfig: BrandingConfig | undefined;
  productCollection: any;
  trackingPageUrl: TrackingPageUrl | undefined

  translations: Translations | undefined
  translations_config: TranslationsConfig | undefined
}

export interface AdditionalTextSetting {
  text_above: string;
  text_below: string;
}

export interface AutoTranslate {
  enable: boolean;
  targetLang: string[];
  langList: LangList[];
  hot: boolean;
  hotText: string;
  returnDefaultLang: boolean;
}

export interface LangList {
  value: string;
  label: string;
}

export interface CustomCSSAndHTML {
  cssCode: string;
  htmlTopCode: string;
  htmlBottomCode: string;
}


export interface TrackingPageUrl {
  subpathPrefix: string
  subpath: string
}

export interface CustomOrderInput {
  days: string;
  trackingInfo: string;
}

export interface CustomOrderStatus {
  ordered: Delivered;
  order_ready: Delivered;
  in_transit: Delivered;
  out_of_delivery: Delivered;
  delivered: Delivered;
}

export interface Delivered {
  icon: string;
  name: string;
  children: Child[];
}

export interface Child {
  id: number;
  status: string;
  info: string;
  days: string;
}

export interface PageTranslations {
  track_your_order: string;
  order_number: string;
  expected_delivery: string;
  or: string;
  email: string;
  track: string;
  order: string;
  tracking_number: string;
  product: string;
  carrier: string;
  status: string;
  order_not_found: string;
  enter_your_order: string;
  enter_your_email: string;
  enter_your_tracking_number: string;
  not_yet_shipped: string;
  ordered: string;
  order_ready: string;
  waiting_update: string;
  pickup_location: string;
  shipping_to: string;
  current_location: string;
  may_like: string;
  pending: string;
  transit: string;
  pickup: string;
  delivered: string;
  expired: string;
  undelivered: string;
  exception: string;
  info_received: string;
}

export interface DateAndTimeFormat {
  date_format: string;
  time_format: string;
}

export enum ThemeContainerWidthType {
  PERCENTAGE = 1, // 百分比
  PIXEL = 2, // 像素
}

/**
 * 追踪页 Theme 区块：布局样式，写入 `displayOption.themeLayout`。
 * 取值与 `tracking_page/theme_config` 的 `theme` 对齐：**0** Classic、**2** Backdrop、**3** Showcase。
 */
export enum ThemeLayoutEnum {
  CLASSIC = 1,
  BACKDROP = 2,
  SHOWCASE = 3,
}

/** `displayOption.theme_id`：接口多为数字；部分路径可能为字符串 */
export type ThemeLayoutId = ThemeLayoutEnum | string

export const normalizeThemeLayoutId = (
  value: ThemeLayoutId | undefined | null,
): ThemeLayoutEnum => {
  if (value === undefined || value === null) {
    return ThemeLayoutEnum.CLASSIC
  }
  const n = typeof value === "string" ? Number.parseInt(value, 10) : value

  if (typeof n !== "number" || !Number.isFinite(n)) {
    return ThemeLayoutEnum.CLASSIC
  }
  if (n === ThemeLayoutEnum.BACKDROP) {
    return ThemeLayoutEnum.BACKDROP
  }
  if (n === ThemeLayoutEnum.SHOWCASE) {
    return ThemeLayoutEnum.SHOWCASE
  }
  return ThemeLayoutEnum.CLASSIC
}

export enum QuotaReminderType {
  PERCENTAGE = 1, // 百分比
  AMOUNT = 2, // 数量
}

export enum UIStyleEnum {
  LIGHT = 0,
  DARK = 1,
}

export enum ProgressBarLayoutEnum {
  HORIZONTAL = 0,
  VERTICAL = 1,
}

/** 主题顶部 Image banner：Shopify Files（Admin GraphQL MediaImage） */
export interface ThemeImageBannerRef {
  /** gid://shopify/MediaImage/... */
  shopifyFileId: string
  /** 预览/展示用 CDN URL */
  url: string
  /** 无障碍描述 */
  alt?: string
}

/** Bottom section：图片宽高比（Square 为默认） */
export type ThemeBottomImageRatio = "AUTO" | "SQUARE" | "PORTRAIT"

/** Bottom section：单张图片（最多 3 张） */
export interface ThemeBottomSectionImageItem {
  id: string
  image?: ThemeImageBannerRef
  destinationUrl: string
  openInNewWindow: boolean
  /** 是否启用该图片位（列表行复选框）；缺省为 true */
  enabled?: boolean
}

/** Bottom section：视频块（YouTube） */
export interface ThemeBottomSectionVideo {
  id: string
  youtubeUrl: string
  alt?: string
  autoplay: boolean
  loop: boolean
  /** 是否启用（列表行复选框）；缺省为 true */
  enabled?: boolean
}

/** Bottom section 整块状态（Theme layout 内容区底部；图片/视频显隐由条目的 enabled 与是否添加决定，与接口字段一致） */
export interface ThemeBottomSectionState {
  images: ThemeBottomSectionImageItem[]
  video?: ThemeBottomSectionVideo
  imageRatio: ThemeBottomImageRatio
}

export interface DisplayOption {
  /** 主题布局样式，缺省按 Classic 处理（接口多为数字，兼容字符串时请用 `normalizeThemeLayoutId`） */
  theme_id?: ThemeLayoutId
  /** Backdrop / Showcase 顶部横幅图（仅存 Shopify 文件 id + URL） */
  themeImageBanner?: ThemeImageBannerRef
  /** Theme layout 底部 Image / Video 区块 */
  themeBottomSection?: ThemeBottomSectionState
  themeContainerWidth: number | string;
  themeContainerWidthType: ThemeContainerWidthType;
  progressBarColor: string;
  progressBarLayout: ProgressBarLayoutEnum;
  uiStyle: UIStyleEnum;
  lookUpOption: LookUpOption;
  carrierDetails: boolean;
  trackingNumber: boolean;
  packageContentsDetails: boolean;
  trackingDetailedInfo: boolean;
  googleTranslateWidget: boolean;
  mapCoordinates: MapCoordinates;
  hideKeywords: string;
  lastMileDelivery: boolean;
  // 是否显示Shipping Protection
  isShowShippingProtection:boolean
  // 是否显示Return Tracking page
  isShowReturns:boolean
  // 是否显示TT评论插件
  isShowReviewOption:boolean

  // 标记勾选框新增一个部分选中的中间态，用来隐藏 CN carriers
  hiddenCountryCarriers: {
    "main": string[],
    "last_mile": string[],
  }
}

export interface LookUpOption {
  BONE: boolean;
  BTN: boolean;
}

export interface MapCoordinates {
  showMap: boolean;
  position: number;
}

export type SelectCountries = {
  "code": string,
  "provinces": { "code": string, "checked"?: boolean }[]
  "all": boolean
}[]

export type TimeRulesType = {
  "date_range": {
    "min": number,
    "max": number
  },
  "countries": SelectCountries
}

type FulfillmentWorkflow = {
  "cutoff_time": {
    "time": string,
    "tz": string
  },
  "business_days": number[],
  "processing_time": number | ""
}

type TransitTime = {
  "rules": TimeRulesType[],
  "default": {
    "date_range": {
      "min": number,
      "max": number
    },
    /** 仅当存在自定义 shipping zone 规则时有效；true 表示禁用，缺省为未禁用 */
    "disabled"?: boolean
  }
}

type NonBusinessDayPeriod = {
  start_date: string
  end_date: string
} | undefined

export interface PostPurchaseEDD {
  "enabled": boolean,
  "calc_from": 1 | 2,
  "fulfillment_workflow": FulfillmentWorkflow,
  "transit_time": TransitTime
  "non_business_day_periods": NonBusinessDayPeriod[]
}

export interface PostEddProvider {
  "id": EDDProviderType,
  "enabled": boolean,
  "sortable": boolean
}

export type PostEddProviders = PostEddProvider[]

export type PostPurchaseEDDModule = Pick<PostPurchaseEDD, "fulfillment_workflow" | "transit_time">

export interface ProRecommendAndUpsell {
  productRecommendations: ProductRecommendations;
  discountData: DiscountData;
}

export interface DiscountData {
  is_show: boolean;
  heading: string;
  description: string;
  discount_code: string;
  button_text: string;
  button_link: string;
  check_time: number;
}

export enum ProductRecommendIntent {
  ShopifyRecommendations= 0,
  SpecificCollection= 1,
  NewestCreated= 2,
  BestSellers = 3,
  FrequentlyBoughtTogether = 4,
}

export interface ProductRecommendations {
  show: boolean;
  position: number;
  adv_coll_enabled: boolean;
  collect: number;
  intent: ProductRecommendIntent;
}

export interface SEOOptimization {
  valueSeoTitle: string;
  valueSeoCon: string;
}

export interface TranslateTrackingDetailedInfo {
  before: string;
  after: string;
  sort?: number;
}

export interface BrandingConfig {
  isDisabled: boolean;
  isRemovedBranding: boolean;
}

export interface Translations {
  [lang: string]: Langs | any
}

export interface Langs {
  track_your_order: string
  order_number: string
  expected_delivery: string
  or: string
  email: string
  track: string
  order: string
  tracking_number: string
  product: string
  carrier: string
  status: string
  order_not_found: string
  enter_your_order: string
  enter_your_email: string
  enter_your_tracking_number: string
  not_yet_shipped: string
  ordered: string
  order_ready: string
  waiting_update: string
  pickup_location: string
  shipping_to: string
  current_location: string
  may_like: string
  pending: string
  transit: string
  pickup: string
  delivered: string
  expired: string
  undelivered: string
  exception: string
  info_received: string
  recommended_title: string
  features_text: FeaturesText
}

export interface FeaturesText {
  custom_status: Status[]
  additional_text_above?: string
  additional_text_below?: string
  custom_tracking_info?: string
}

export interface Status {
  status?: string
  info?: string
}

export interface TranslationsConfig {
  auto_switch: boolean
  primary_locale: string
  locales: Locale[]
}

export interface Locale {
  name: string
  value: string
}
